/**
 * Centralized number formatting utilities for Italian locale (it-IT).
 * All number display in the application should use these functions
 * to ensure consistent formatting with Italian conventions:
 * - Dot (.) as thousands separator
 * - Comma (,) as decimal separator
 */

/**
 * Display mode for currency formatting.
 * - 'default': Standard currency format (€ 15.000,00)
 * - 'delta': Shows sign prefix for differences (+€ 1.234,56 / -€ 1.234,56)
 */
export type CurrencyDisplayMode = 'default' | 'delta';

/** Shared Intl.NumberFormat instance for currency amounts (2 decimals) */
const CURRENCY_FORMATTER = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Shared Intl.NumberFormat instance for hours (max 1 decimal) */
const HOURS_FORMATTER = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Shared Intl.NumberFormat instance for percentages (2 decimals) */
const PERCENT_FORMATTER = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a numeric value as Italian-locale EUR currency.
 *
 * @param value - The numeric amount (nullable)
 * @param mode - Display mode: 'default' for standard, 'delta' for signed differences
 * @param fallback - Optional fallback string for null/undefined (default: "€ 0,00")
 * @returns Formatted currency string with € prefix
 *
 * @example
 * formatCurrency(15000)              // "€ 15.000,00"
 * formatCurrency(1234.56, 'delta')   // "+€ 1.234,56"
 * formatCurrency(-1234.56, 'delta')  // "-€ 1.234,56"
 * formatCurrency(0, 'delta')         // "€ 0,00"
 */
export function formatCurrency(
  value: number | null | undefined,
  mode: CurrencyDisplayMode = 'default',
  fallback?: string,
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return fallback ?? "€ 0,00";
  }

  if (mode === 'delta') {
    if (value === 0) {
      return "€ 0,00";
    }
    const formatted = CURRENCY_FORMATTER.format(Math.abs(value));
    return value > 0 ? `+€ ${formatted}` : `-€ ${formatted}`;
  }

  return `€ ${CURRENCY_FORMATTER.format(value)}`;
}

/**
 * Formats a numeric value as hours with Italian locale.
 * Rounds to max 1 decimal. Appends "h" suffix.
 * Output: "1.523,5h" or "15,5h" or "0h" for null/undefined.
 *
 * @param value - The hour value (nullable)
 * @param fallback - Optional fallback string for null/undefined (default: "0h")
 * @returns Formatted hours string with "h" suffix
 */
export function formatHours(
  value: number | null | undefined,
  fallback?: string,
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return fallback ?? "0h";
  }
  return `${HOURS_FORMATTER.format(value)}h`;
}

/**
 * Formats a numeric value as a percentage with Italian locale.
 * Always shows exactly 2 decimals. Appends "%" suffix.
 * Output: "85,50%" or "0,00%" for null/undefined.
 *
 * @param value - The percentage value (nullable), e.g. 85.5 for 85,50%
 * @param fallback - Optional fallback string for null/undefined (default: "0,00%")
 * @returns Formatted percentage string with "%" suffix
 */
export function formatPercent(
  value: number | null | undefined,
  fallback?: string,
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return fallback ?? "0,00%";
  }
  return `${PERCENT_FORMATTER.format(value)}%`;
}
